import React, { useMemo, useState } from "react";
import {
  Box,
  Link as MuiLink,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";

import { useAuth } from "../context/authContext";
import useApi from "../hooks/useApi";
import {
  useContentMetadata,
  useJournalForContents,
} from "../hooks/useCustomQuery";
import { demoJournal } from "../utils/utils";
import JournalEntry from "../features/track/JournalEntry";
import LibraryBanner from "../features/track/LibraryBanner";
import TVSeriesJournalSummary from "../features/track/TVSeriesJournalSummary";
import { StyledLink } from "./CustomMUI";

/**
 * The "You" tab of LibraryModal: the viewer's own journal entries for the
 * active content (every season merged into one list on a TV series), or a
 * demo journal when signed out.
 *
 * `scopedEntryId` is set when the viewer arrived at one of their own entries
 * directly (a track page tile, or their own shared link) — the list starts
 * narrowed to just that entry, with a link to reveal the rest. LibraryModal
 * clears it on any status change, so a newly created entry is never hidden.
 */
export default function ContentJournal({
  fullscreen,
  contentType,
  scopedEntryId,
  activeContentId,
  status,
  onExitScope,
  onStatusChange,
  onContentChange,
  onRequestStatusMenu,
}) {
  const { user, login } = useAuth();
  const { fetchTVJournalSummary } = useApi();
  const metadataQuery = useContentMetadata(contentType, activeContentId);
  const metadata = metadataQuery.data;

  // Derive seriesId from metadata (season_number === 0)
  const tvSeriesId =
    contentType === "tvshow" && metadata
      ? metadata.seasons?.find((season) => season.season_number === 0)
          ?.content_id
      : null;

  const isTVSeries = contentType === "tvshow" && tvSeriesId === activeContentId;

  // On the series tab, show entries for every season plus the series itself,
  // merged into one list. On a season tab, also fetch the series' own entries
  // (without displaying them) so the empty state below can tell whether this
  // season's status was inherited from a series-level "finish" rather than
  // tracked here directly — see seriesFinishedEntry. Movies/books just fetch
  // the one content id.
  const journalContentIds = useMemo(() => {
    if (isTVSeries)
      return metadata?.seasons?.map((season) => season.content_id) ?? [];
    if (contentType === "tvshow" && tvSeriesId)
      return [activeContentId, tvSeriesId];
    return [activeContentId];
  }, [isTVSeries, metadata, activeContentId, contentType, tvSeriesId]);

  // Maps each season's content id to its season number. Drives both the gutter
  // label and the season tie-break when ordering a merged series list, so the
  // two can't disagree. Null outside the series view, which is what tells
  // useJournalForContents to skip season ordering entirely.
  const seasonNumberById = useMemo(() => {
    if (!isTVSeries || !metadata?.seasons) return null;
    return metadata.seasons.reduce((acc, season) => {
      acc[season.content_id] = season.season_number;
      return acc;
    }, {});
  }, [isTVSeries, metadata]);

  const { entries: journalEntries, isLoading: journalLoading } =
    useJournalForContents(journalContentIds, {
      enabled: !!user,
      seasonNumberById,
    });

  // The series' own entries are only fetched (on a season tab) to answer
  // seriesFinishedEntry below — they never render as part of this season's
  // own list, so filter them back out here.
  const journal = useMemo(() => {
    if (!user) return demoJournal[contentType] ?? [];
    if (isTVSeries) return journalEntries;
    return journalEntries.filter(
      (entry) => entry.content_id === activeContentId,
    );
  }, [user, journalEntries, contentType, isTVSeries, activeContentId]);

  // The scope lives only in the URL (via scopedEntryId) — revealing the rest
  // of the list clears it there, so a refresh never re-narrows an expanded
  // list. Only scoped while the entry is actually in this list, so a scope to
  // an entry that's since gone falls back to the full list rather than nothing.
  const isScoped =
    scopedEntryId != null &&
    journal.some((entry) => entry.journal_id === scopedEntryId);

  const visibleJournal = isScoped
    ? journal.filter((entry) => entry.journal_id === scopedEntryId)
    : journal;
  const hiddenEntryCount = journal.length - visibleJournal.length;

  // Prompts always target the newest entry, never a scoped older one — the
  // newest is what a status change just created and wants filling in.
  const newestEntryId = journal[0]?.journal_id ?? null;

  // True on the "All" tab when the merged list is made up solely of the
  // series' own entry/entries (the series was finished directly and no season
  // has ever been logged individually) — every stat tile below is either
  // trivially derived from that one action or empty, so the summary adds
  // nothing beyond what the entry itself already shows.
  const seriesEntryOnly =
    isTVSeries &&
    journal.length > 0 &&
    journal.every((entry) => entry.content_id === tvSeriesId);

  // True when this season is itself finished but has no journal entries of its
  // own, while the series it belongs to has a finished entry — i.e. this
  // season's "finished" status was inherited via
  // propagate_tvseries_finished_to_seasons rather than tracked here directly.
  // The season's own status must also be checked: without it, a season the
  // user later moved off "finished" would still show this message purely
  // because the series was finished at some point in the past.
  // Drives the empty-state message below.
  const seriesFinishedEntry =
    contentType === "tvshow" &&
    !isTVSeries &&
    !!tvSeriesId &&
    status === "finished"
      ? journalEntries.some(
          (entry) =>
            entry.content_id === tvSeriesId && entry.status === "finished",
        )
      : false;

  // True on the "All" tab when the series' own status is finished but no
  // journal entry exists for the series content id itself — whether a
  // genuine series-wide finish that was never logged (e.g. a legacy direct
  // write predating derived statuses) or a series that reached "finished"
  // purely by every season being finished individually. Either way there's
  // no entry to show for the series itself, so this offers to add one.
  const seriesUnloggedFinish =
    isTVSeries &&
    status === "finished" &&
    journal.every((entry) => entry.content_id !== tvSeriesId);

  // Shared by both "log again" links below (season-level and series-level) —
  // they're mutually exclusive (one only shows on a season tab, the other
  // only on the series tab) so a single pending state covers both safely.
  // onStatusChange defaults its target to whichever content id is active, so
  // no explicit contentId is needed here either way.
  const [loggingAgain, setLoggingAgain] = useState(false);

  const handleLogAgain = async () => {
    if (loggingAgain) return;
    setLoggingAgain(true);
    try {
      await onStatusChange?.("logAgain");
    } catch (e) {
      // error already surfaced by the mutation's query client handling
    } finally {
      setLoggingAgain(false);
    }
  };

  const tvJournalSummary = useQuery({
    queryKey: ["TVJournalSummary", tvSeriesId],
    queryFn: () => fetchTVJournalSummary(tvSeriesId),
    enabled: contentType === "tvshow" && !!tvSeriesId && !!user,
  });

  // TVSeriesJournalSummary renders nothing when the show has no activity, so
  // this has to be known here too: without it the wrapper below still renders
  // as an empty flex child and contributes a full `gap` above the empty state.
  const summaryHasActivity = !!tvJournalSummary.data?.has_activity;

  if (journalLoading || tvJournalSummary.isLoading) {
    return (
      <Box sx={{ mt: 2 }}>
        <Stack direction="column" spacing={1}>
          <Skeleton width="70%" />
          <Skeleton width="60%" />
        </Stack>
      </Box>
    );
  }

  return (
    // gap is the sole source of inter-entry spacing — JournalEntry no longer
    // adds its own margin, so entries separate cleanly and consistently.
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
      {!user && (
        <LibraryBanner
          content={
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                textAlign: { xs: "left", sm: "center" },
              }}
            >
              This is a demo journal,{" "}
              <MuiLink
                component="span"
                onClick={login}
                sx={{ cursor: "pointer" }}
              >
                create an account
              </MuiLink>{" "}
              to log your own rating, reactions and notes
            </Typography>
          }
          gap={0}
        />
      )}

      {/* The series summary describes the whole list, so it waits until a
          scoped list has been expanded. */}
      {isTVSeries &&
        !!user &&
        !isScoped &&
        ((!seriesEntryOnly && summaryHasActivity) || seriesUnloggedFinish) && (
          <Box>
            {!seriesEntryOnly && (
              <TVSeriesJournalSummary summary={tvJournalSummary.data} />
            )}
            {seriesUnloggedFinish && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 0.5,
                }}
              >
                <Typography variant="body2" color="text.disabled">
                  Tracking by season
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  <StyledLink
                    component="span"
                    onClick={loggingAgain ? undefined : handleLogAgain}
                    sx={{ cursor: loggingAgain ? "default" : "pointer" }}
                  >
                    Log entry for whole series
                  </StyledLink>
                </Typography>
              </Box>
            )}
          </Box>
        )}

      {visibleJournal.map((entry) => (
        // layout="position" so an entry slides to its new place when a date
        // change re-sorts the list (or a scoped list expands around it),
        // rather than jumping. Keyed on journal_id (rather than index) so React
        // reorders the existing instances instead of remounting them —
        // JournalEntry seeds its local edit state from props only once, so a
        // remount here would discard an in-flight edit.
        <motion.div key={entry.journal_id} layout="position">
          <JournalEntry
            contentType={entry.content_type}
            contentId={entry.content_id}
            existingEntry={entry}
            isMobile={fullscreen}
            seasonNumber={seasonNumberById?.[entry.content_id] ?? null}
            onDelete={() => {
              // Deleting the entry this list is scoped to leaves nothing to
              // scope to — drop back to the full list rather than closing.
              if (entry.journal_id === scopedEntryId) onExitScope?.();
            }}
            isOwn={!!user}
            showPrompt={!!user && entry.journal_id === newestEntryId}
          />
        </motion.div>
      ))}

      {hiddenEntryCount > 0 && (
        <Typography variant="body2" color="text.disabled">
          <StyledLink component="span" onClick={() => onExitScope?.()}>
            Show {hiddenEntryCount} other{" "}
            {hiddenEntryCount === 1 ? "entry" : "entries"}
          </StyledLink>
        </Typography>
      )}

      {journal.length === 0 && user && (
        <Typography variant="body2" color="text.disabled">
          {seriesFinishedEntry ? (
            <>
              Finished via the{" "}
              <StyledLink
                component="span"
                onClick={() => onContentChange?.(tvSeriesId)}
              >
                series
              </StyledLink>
              {" · "}
              <StyledLink
                component="span"
                onClick={loggingAgain ? undefined : handleLogAgain}
                sx={{ cursor: loggingAgain ? "default" : "pointer" }}
              >
                Log this season individually
              </StyledLink>
            </>
          ) : contentType === "tvshow" && !isTVSeries ? (
            <>
              No season-level tracking yet.{" "}
              <StyledLink
                component="span"
                onClick={() => onRequestStatusMenu?.()}
              >
                {status === "none" ? "Add a" : "Update the"} status
              </StyledLink>{" "}
              to begin.
            </>
          ) : (
            // Every card type forwards statusMenuOpenSignal to its
            // ContentCardActions, so this link has a listener whatever the
            // content is (see LibraryModal).
            <>
              No tracking yet.{" "}
              <StyledLink
                component="span"
                onClick={() => onRequestStatusMenu?.()}
              >
                {status === "none" ? "Add a" : "Update the"} status
              </StyledLink>{" "}
              to begin.
            </>
          )}
        </Typography>
      )}
    </Box>
  );
}
